/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — live multiplayer: drag broadcast + remote cursors.
   Ported behavior-identical from the old OrgChart.tsx multiplayer
   section (33ms drag throttle / 30fps, 50ms cursor throttle / 20fps,
   5s cursor TTL, skip-local-dragging-ids on inbound apply).
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Node } from '@xyflow/react';
import {
  sendOrgChartDrag,
  sendOrgChartCursor,
  sendOrgChartCursorLeave,
  onOrgChartDrag,
  onOrgChartCursor,
} from '../../../../services/liveSync';

export interface RemoteCursor {
  x: number;
  y: number;
  userName: string;
}

export function useMultiplayer(rfSetNodes: (updater: (nds: Node[]) => Node[]) => void) {
  const localDraggingIds = useRef<Set<string>>(new Set());
  const lastDragSendAt = useRef(0);
  const lastCursorSendAt = useRef(0);
  const remoteCursorTtl = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [remoteCursors, setRemoteCursors] = useState<Record<string, RemoteCursor>>({});

  useEffect(() => {
    const offDrag = onOrgChartDrag((msg) => {
      rfSetNodes((nds) =>
        nds.map((n) => {
          if (localDraggingIds.current.has(n.id)) return n;
          const remote = msg.nodes.find((r) => r.id === n.id);
          if (!remote) return n;
          return { ...n, position: { x: remote.x, y: remote.y } };
        })
      );
    });
    const offCursor = onOrgChartCursor((msg) => {
      const existing = remoteCursorTtl.current.get(msg.userId);
      if (existing) clearTimeout(existing);
      if (msg.leave) {
        setRemoteCursors((prev) => {
          if (!prev[msg.userId]) return prev;
          const next = { ...prev };
          delete next[msg.userId];
          return next;
        });
        remoteCursorTtl.current.delete(msg.userId);
        return;
      }
      setRemoteCursors((prev) => ({
        ...prev,
        [msg.userId]: { x: msg.x, y: msg.y, userName: msg.userName },
      }));
      const t = setTimeout(() => {
        setRemoteCursors((prev) => {
          if (!prev[msg.userId]) return prev;
          const next = { ...prev };
          delete next[msg.userId];
          return next;
        });
        remoteCursorTtl.current.delete(msg.userId);
      }, 5000);
      remoteCursorTtl.current.set(msg.userId, t);
    });
    return () => {
      offDrag();
      offCursor();
      sendOrgChartCursorLeave();
      for (const t of remoteCursorTtl.current.values()) clearTimeout(t);
      remoteCursorTtl.current.clear();
    };
  }, [rfSetNodes]);

  /** Throttled (30fps) broadcast of active/final drag positions. */
  const broadcastDrag = useCallback(
    (
      payload: Array<{ id: string; x: number; y: number; type: 'person' | 'junction' }>,
      active: boolean
    ) => {
      if (active) {
        const now = performance.now();
        if (now - lastDragSendAt.current < 33) return;
        lastDragSendAt.current = now;
      }
      sendOrgChartDrag(payload, active);
    },
    []
  );

  const onCanvasMouseMove = useCallback(
    (screenToFlowPosition: (p: { x: number; y: number }) => { x: number; y: number }) =>
      (e: React.MouseEvent<HTMLDivElement>) => {
        const now = performance.now();
        if (now - lastCursorSendAt.current < 50) return;
        lastCursorSendAt.current = now;
        const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        sendOrgChartCursor(pos.x, pos.y);
      },
    []
  );

  const onCanvasMouseLeave = useCallback(() => {
    sendOrgChartCursorLeave();
  }, []);

  return {
    remoteCursors,
    localDraggingIds,
    broadcastDrag,
    onCanvasMouseMove,
    onCanvasMouseLeave,
  };
}
